import React from 'react';
import { Editor, NodeEntry, Node, Path, Transforms, Element } from 'slate';
import { theme } from '@ublend-npm/aulaui-next';
import { AulaPlugin } from '../plugins';
import { inlineLinkType, isInlineLink } from './inlineLinkType';
import {
  Anchor,
  PaddedSpan,
} from '../../components/InlineLink/InlineLink.styled';
import { getCurrentFragment } from '../../lib/queries';

const InlineChromiumBugfix = () => <span contentEditable={false} />;

export const inlineLink: AulaPlugin = (editableProps, editor) => {
  const { isInline, normalizeNode } = editor;
  editor.isInline = (el) => el.type === inlineLinkType || isInline(el);

  editor.normalizeNode = (entry: NodeEntry<Node>) => {
    const [node, path] = entry;
    if (Element.isElement(node) && isInlineLink(node)) {
      const parentNode = Node.parent(editor, path);
      const islastChild =
        path[path.length - 1] === parentNode.children.length - 1;

      const nextPath = Path.next(path);

      if (islastChild) {
        Transforms.insertNodes(
          editor,
          { text: '', inlineLinkTerminus: true },
          { at: nextPath, select: true }
        );
        return;
      }
    }

    normalizeNode(entry);
  };

  // TODO: Re-enable with below
  // let lastActiveSelection: Range | null = null;

  return {
    ...editableProps,
    // there's a Slate bug in the current version we're using (^0.59.0)
    // that's preventing the decorator to rerender the text
    // the bug appears to be fixed in the latest version of Slate (0.62.1)
    // decorators will work when we upgrade to the latest Slate version

    /**
     * TODO: Currently disabled because it's not updating correctly
     * Re-enable once #13396 is fixed or sunset this feature
     *
     decorate: ([node, path]) => {
      if (
        Text.isText(node) &&
        node.text !== '' &&
        editor.selection == null &&
        lastActiveSelection != null
      ) {
        const intersection = Range.intersection(
          lastActiveSelection,
          Editor.range(editor, path)
        );

        if (intersection == null) {
          return [];
        }

        const range = {
          ...intersection,
          highlight: true,
        };
        return [range];
      }
      return [];
    },

     */
    renderLeaf: (props) => {
      const { children, leaf, attributes } = props;

      if (leaf.highlight) {
        return (
          <span
            {...attributes}
            style={{
              background: theme.color.designSystem.purple30,
              padding: '2px 0',
            }}
          >
            {children}
          </span>
        );
      }

      // This is required to work around the Chromium bug
      // https://github.com/ianstormtaylor/slate/issues/4704#issuecomment-1006696364
      // where users cannot break out of inline nodes at the end of the editor.
      // The solution is based on the official Slate example
      // https://github.com/ianstormtaylor/slate/blob/main/site/examples/inlines.tsx
      if (
        typeof navigator !== 'undefined' &&
        /Chrome/i.test(navigator.userAgent) &&
        leaf.inlineLinkTerminus
      ) {
        return <PaddedSpan {...attributes}>{children}</PaddedSpan>;
      }

      if (editableProps.renderLeaf) {
        return editableProps.renderLeaf(props);
      }

      return <span {...attributes}>{children}</span>;
    },
    renderElement: (props) => {
      const { element, attributes, children } = props;

      // eslint-disable-next-line react-hooks/rules-of-hooks
      // TODO: Re-enable together with above
      // useEffect(() => {
      //   if (editor.selection != null) lastActiveSelection = editor.selection;
      //   // eslint-disable-next-line react-hooks/exhaustive-deps
      // }, [editor.selection]);

      if (element.type !== inlineLinkType && editableProps.renderElement) {
        return editableProps.renderElement(props);
      }

      // Contents need to be wrapped with an additional span to work around the
      // Chromium issue where selection cannot be placed at the start of an inline node:
      // https://bugs.chromium.org/p/chromium/issues/detail?id=1249405.
      // The fix is based on the official Slate example
      // https://github.com/ianstormtaylor/slate/blob/main/site/examples/inlines.tsx

      return (
        <Anchor target="_blank" href={element.url} {...attributes}>
          <InlineChromiumBugfix />
          {children}
          <InlineChromiumBugfix />
        </Anchor>
      );
    },
    onKeyDown: (event) => {
      if (
        event.key !== 'Enter' ||
        !isInlineLink(getCurrentFragment(editor)[0])
      ) {
        editableProps.onKeyDown?.(event);
        return;
      }

      const { selection } = editor;
      const isEnd = selection
        ? Editor.isEnd(editor, selection.anchor, selection.anchor.path)
        : false;
      if (!isEnd) {
        editableProps.onKeyDown?.(event);
        return;
      }

      event.preventDefault();
    },
  };
};
